import { apiFetch } from "./index";
import { getApiBaseUrl } from "./baseUrl";

const API_BASE = getApiBaseUrl();

export type RestartRouteReason =
  | "time_changed"
  | "priority_changed"
  | "unexpected_event"
  | "need_smaller_step"
  | "other";

export type RestartRouteBucket =
  | "this_week_minimum"
  | "if_time_allows"
  | "next_week"
  | "later";

export interface RestartRouteTaskDates {
  planned_start: string | null;
  deadline: string | null;
}

export interface RestartRouteTask {
  task_id: number;
  title: string;
  reason?: string;
  before?: RestartRouteTaskDates;
  after?: RestartRouteTaskDates | null;
}

export interface RestartRouteCandidate {
  task_id: number;
  title: string;
  before: RestartRouteTaskDates;
}

export interface RestartRouteChange extends RestartRouteTask {
  task_type: "monthly_ai_task" | "weekly_ai_task";
  before: RestartRouteTaskDates;
  after: RestartRouteTaskDates | null;
  changed: boolean;
  reason: string;
}

export interface RestartRoutePreview {
  summary: string;
  this_week_minimum: RestartRouteTask[];
  if_time_allows: RestartRouteTask[];
  next_week: RestartRouteTask[];
  later: RestartRouteTask[];
  monthly_outlook: {
    status: "unchanged" | "on_track" | "adjusted" | "needs_review";
    message: string;
  };
  feasibility?: {
    status: "ready" | "needs_review";
    message: string;
    adjustment_window_start: string;
    adjustment_window_end: string;
    protected_month_start: string;
    candidate_task_ids: number[];
    candidates: RestartRouteCandidate[];
    selected_task_ids: number[];
    changes: RestartRouteChange[];
    active_plan_count: number;
    weekly_global_limit: number;
    per_plan_week_limit: number;
  };
}

export interface RestartRoutePreviewResponse {
  plan: {
    id: number;
    title: string;
    goal: string;
    target_date: string | null;
  };
  selected_task_ids: number[];
  preview: RestartRoutePreview;
  generated_at: string;
  is_preview: true;
}

/**
 * 今月の未完了週次タスクを候補から明示的に選び、今週より後ろ〜来月末の範囲で調整する案を取得する。
 * task_ids を空配列で渡すと候補だけを取得できる。反映は applyRestartRoute を明示的に呼んだ場合だけ行う。
 */
export const getRestartRoutePreview = async (
  token: string,
  request: {
    plan_id: number;
    task_ids?: number[];
    // 旧クライアント互換。新しい画面では使用しない。
    this_week_task_capacity?: number;
    reason?: RestartRouteReason;
  }
) => {
  return await apiFetch<RestartRoutePreviewResponse>(
    `${API_BASE}/api/management/restart-route/preview`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(request),
      hideGlobalLoader: true,
    }
  );
};

export interface RestartRouteDecision {
  task_id: number;
  bucket: RestartRouteBucket;
}

export interface ApplyRestartRouteResponse {
  revision: { id: number; created_at: string };
  changed_tasks: Array<{
    task_id: number;
    planned_start: string | null;
    deadline: string | null;
    priority: number;
    bucket?: RestartRouteBucket;
  }>;
  message: string;
}

export const applyRestartRoute = async (
  token: string,
  request: {
    plan_id: number;
    summary?: string;
    task_ids: number[];
    expected_changes?: Array<{
      task_id: number;
      before: RestartRouteTaskDates;
      after: RestartRouteTaskDates;
    }>;
    // 旧クライアント互換。新しい画面では使用しない。
    this_week_task_capacity?: number;
    decisions?: RestartRouteDecision[];
  }
) => {
  return await apiFetch<ApplyRestartRouteResponse>(
    `${API_BASE}/api/management/restart-route/apply`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(request),
      hideGlobalLoader: true,
    }
  );
};

export interface UndoRestartRouteResponse {
  revision_id: number;
  restored_task_ids: number[];
  message: string;
}

export const undoRestartRoute = async (token: string, revisionId: number) => {
  return await apiFetch<UndoRestartRouteResponse>(
    `${API_BASE}/api/management/restart-route/revisions/${revisionId}/undo`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({}),
      hideGlobalLoader: true,
    }
  );
};
